import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import HTabButton from './HTabButton';

const defaultContentBoxStyles = {
  px: '8',
  py: '7',
  bgColor: 'white',
  borderRadius: 'lg',
  height: '90%',
  minHeight: '650px',
  overflow: 'auto',
  marginBottom: '0.5rem'
};

const HorizontalTabs = ({
  paddingLeft, center, children,
  tabGroupWidth, defaultTabId,
  contentBoxStyles, minHeight,
  sidePanel, tabWidth,
  extendDefaultContentBoxStyles,
  tabIdParam,
  tabMarginRight, specificTabGroupProps = {},
  aiEnabled = false
}) => {
  const { [tabIdParam]: tabIdFromParams } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const [currentTab, setCurrentTab] = useState(null);

  useEffect(() => {
    if (!children.length) return;

    const tabId = defaultTabId ?? tabIdFromParams;
    const tab = children.find(child => child.props.tabId === tabId);

    if (!tab) {
      throw new Error(`Tab with id ${tabId} not found`);
    }

    setCurrentTab(tab.props);
  }, [defaultTabId, tabIdFromParams]);

  const contentStyles = useMemo(() => {
    if (!currentTab) return {};

    // specific logic for marketing AI tab content
    if (currentTab.tabId === 'question-templates') {
      if (aiEnabled) {
        contentBoxStyles.bgColor = 'white';
      } else {
        contentBoxStyles.bgColor = 'black';
        contentBoxStyles.color = 'white';
      }
    } else {
      contentBoxStyles.bgColor = 'white';
    }

    if (!extendDefaultContentBoxStyles) return contentBoxStyles;
    return { ...defaultContentBoxStyles, ...contentBoxStyles };
  }, [currentTab, contentBoxStyles, extendDefaultContentBoxStyles]);

  const handleTabClick = clicked => {
    if (clicked.onTabSelect) {
      clicked.onTabSelect();
    }

    if (clicked.tabId === currentTab.tabId) return;

    if (!tabIdFromParams) {
      setCurrentTab(clicked);
      return;
    }

    history.push(url.replace(currentTab.tabId, clicked.tabId));
  };

  if (!currentTab?.main) return null;

  const { main: TabContent } = currentTab;

  const tabs = currentTab ? children.map((child, idx) => (
    <HTabButton
      cypressId={child.props.cypressId}
      icon={child.props?.icon}
      idx={idx}
      isActive={child.props.tabId === currentTab.tabId}
      isLast={idx + 1 === children.length}
      key={child.props.title}
      tabMarginRight={tabMarginRight}
      title={child.props.title}
      w={tabWidth || undefined}
      onClick={() => handleTabClick(child.props)}
    />
  )) : null;

  return (
    <Box
      height="80vh"
      minH={minHeight}
      position="relative"
      width="100%"
    >
      <Flex
        id="parent-target"
        justifyContent="flex-start"
        margin={center ? '0 auto' : ''}
        pl={center ? 0 : paddingLeft}
        sx={specificTabGroupProps}
        width={tabGroupWidth}
      >
        {tabs}
      </Flex>
      <Flex {...contentStyles}>
        {sidePanel && <Box flexBasis="25%">{sidePanel}</Box>}
        <Box overflow="auto" width="100%">
          <TabContent />
        </Box>
      </Flex>
    </Box>
  );
};

const HTab = ({ children }) => <>{children}</>;

HorizontalTabs.defaultProps = {
  paddingLeft: '15px',
  center: false,
  contentBoxStyles: defaultContentBoxStyles,
  tabIdParam: 'tabId'
};

HorizontalTabs.propTypes = {
  /** The name of the url param that will be used to determine which tab is active */
  tabIdParam: PropTypes.string,
  /** Left padding needed if a menu should be offset, i.e. when horizontal tabs are used with vertical `TabMenu` */
  paddingLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** If tabs are not offset, `center` should be set to true so that tabs are centered above the content container */
  center: PropTypes.bool,
  /** Array of `HTab` components that are the tabs themselves */
  children: PropTypes.arrayOf(PropTypes.element),
  /** Width of the tabs' parent container. Will typically be slightly smaller than the width of the content container.
   * For example, if the content container is 400px wide, the tabGroupWidth might be 350px */
  tabGroupWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Tab Content styling * */
  contentBoxStyles: PropTypes.shape({}),
  defaultTabId: PropTypes.string,
  /** Minimum height for the container that wraps the tab content * */
  minHeight: PropTypes.string,
  /** The width of each tab * */
  tabWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Optional side panel that can be passed in to be rendered to the left of the tab content */
  sidePanel: PropTypes.element,
  /** contentBoxStyles styles should extend rather than replace default content box styles */
  extendDefaultContentBoxStyles: PropTypes.bool,
  /** Spacing between tabs.  Excludes last tab */
  tabMarginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Props to be passed to the parent container of the tabs */
  specificTabGroupProps: PropTypes.shape({}),
  /** If true, the AI tab content will be rendered with a white background */
  aiEnabled: PropTypes.bool
};

export { HorizontalTabs, HTab };
export default HorizontalTabs;
